import { batch, lazy, Switch, Match, Show, onMount, onCleanup, createEffect, createSignal } from "solid-js"
import { useHeroForm } from "../contexts/HeroFormContext"

// The customisation modal (fit/size/measurement/style pickers + Motion One) is only needed once the
// visitor opens it. It is code-split, prefetched when the main thread is idle, and mounted on first
// open; it then stays mounted so Presence exit animations keep working on subsequent opens.
const loadCustomizationModal = () => import("./CustomizationModal");
const MobileCustomizationsModal = lazy(() => loadCustomizationModal().then(module => ({ default: module.MobileCustomizationsModal })));
const MODAL_PREFETCH_IDLE_TIMEOUT_MS = 3000;

// const DesktopCustomizationsModal = lazy(() => import('./CustomizationModal').then(module => ({ default: module.DesktopCustomizationsModal })))
// const MobileCustomizationsModal = lazy(() => import('./CustomizationModal').then(module => ({ default: module.MobileCustomizationsModal })))

import { OptionCard } from "./OptionCard"
import { HeroFormSubmitGroup } from "./HeroFormSubmitGroup"
import { ValidationBar } from "../components/ValidationBar"
import { getCurrentFormCompletionStatus, getFirstInvalidMeasurementCursor } from "../js/variantsUtil"
import { SizeButtonWithTabs } from "./SizeButtonWithTabs";
import { SizeButtonV2 } from "./SizeButtonV2"
import { CustomizationStatus, CustomizationStatusV2 } from '../components/CustomisationStatus'
import { FeaturedReviewsContainer } from "./ReviewContainer"
import { MoneyBackGuarantee } from "../components/MoneyBackGuarantee"
import { BrandValues, BrandValuesV2 } from "./BrandValues"
import { Stars } from "../components/StarIcon"
import { getCurrencySymbol } from "../js/itemPropsHelper"
import { twMerge } from "tailwind-merge"
import { ColorStatus } from "../components/ColorStatus"
import { AccentContainerV2 } from "./AccentsContainer"
import { RightArrow } from "../components/RightArrowIcon"
// "Reasons to buy" (icons, story, video) sits below the CTA and has no controls of its own: code-split
// and mounted only when it comes within a screen of the viewport, like the reviews block.
const RTB = lazy(() => import("./RTB").then(module => ({ default: module.RTB })));
const RTB_MOUNT_ROOT_MARGIN = '600px 0px';
import { CustomizationMainForm } from "./CustomizationMainForm"
import { TrustPolicy } from "./TrustPolicy"

export const MobileCustomizationFields = (props) => {
  const selectedOption = () =>
    props.customization.options[props.customization.selected];
  const {
    navBarData: {
      handlers: { handleNavBarChange, handleNavBarVisiblity },
    },
  } = useHeroForm();

  const chargeVariant = () => selectedOption()?.chargeVariant;

  const handleSelection = () => {
    batch(() => {
      handleNavBarChange(handleize(props.customization.label));
      handleNavBarVisiblity(true);
    });
  };

  return (
    <div
      class={
        twMerge(
          "w-full flex flex-col border-b-inner border-solid border-frame px-2 py-2 cursor-pointer",
          props.class ?? ""
        )
      }
      classList={{ 'sm:hidden': props.showOnDesktop === undefined || !props.showOnDesktop }}
      onClick={handleSelection}
    >
      <div class="flex-auto flex justify-end items-center gap-2 pt-1">
        <Show
          when={
            selectedOption()?.thumbnailSrc != undefined &&
            selectedOption()?.thumbnailSrc?.trim()?.length > 0
          }
          fallback={<div class="h-5 w-5"></div>}
        >
          <div
            class="h-5 w-5 inline-svg-container text-ink"
            innerHTML={selectedOption()?.thumbnailSrc}
          />
        </Show>
        <div className="flex flex-col grow items-stretch">
          <div class="flex-auto font-bold  text-base">
            {props.customization.label}
          </div>
          <div class="grow w-max">{selectedOption().displayName || selectedOption().label}</div>
          <Show when={chargeVariant()}>
            <span
              class="bg-frame-dark text-frame-light px-0.5 rounded-md text-xs w-fit"
            >{getCurrencySymbol()}+</span>
          </Show>
        </div>
        <div>
          <RightArrow />
        </div>
      </div>
    </div>
  );
};


const MobileCustomizationFieldsTwoColumns = (props) => {
  const selectedOption = () =>
    props.customization.options[props.customization.selected];
  const {
    navBarData: {
      handlers: { handleNavBarChange, handleNavBarVisiblity },
    },
  } = useHeroForm();

  const handleSelection = () => {
    batch(() => {
      handleNavBarChange(handleize(props.customization.label));
      handleNavBarVisiblity(true);
    });
  };
  return (
    <Show when={props.type === "STYLE"}>
      <div
        class="w-full flex flex-col border-b-inner odd:border-r-inner odd:last:border-r-0 odd:last:col-span-2 border-frame px-2 py-2 cursor-pointer"
        onClick={handleSelection}
      >
        <div class="flex-auto flex justify-end items-center gap-1 pt-1">
          <Show
            when={
              selectedOption().thumbnailSrc != undefined &&
              selectedOption().thumbnailSrc.trim().length > 0
            }
            fallback={<div class="h-5 w-5"></div>}
          >
            <div
              class="size-5 sm:size-6 inline-svg-container shrink-0 text-ink"
              innerHTML={selectedOption().thumbnailSrc}
            />
          </Show>
          <div className="flex flex-col grow items-stretch">
            <div class="flex-auto font-bold  text-sm sm:text-base">
              {props.customization.label}
            </div>
            <div class="grow whitespace-break-spaces text-xs sm:text-sm">
              {selectedOption().label}
            </div>
          </div>
          <div>
            <RightArrow />
          </div>
        </div>
      </div>
    </Show>
  );
};

const SizeButton = () => {
  const {
    navBarData: {
      handlers: { handleNavBarChange, handleNavBarVisiblity },
    },
    fit: { fit },
    sizeSettings: {
      errorMessage,
      diyData,
      measurementStatus,
      measurementOptions,
      sendRefData,
      standardSizeData,
      diyFormUtils: {
        formCursor,
        handlers: { setFormCursor },
      },
      handlers: { handleErrorMessage },
    },
    constants: { FORM_COMPLETION_STATUS },
  } = useHeroForm();

  const handleClick = () => {
    batch(() => {
      handleNavBarChange("size");
      handleNavBarVisiblity(true);

      if (measurementOptions()["selected"] == "measure-it-yourself") {
        const currentFormCompletionStatus = getCurrentFormCompletionStatus(
          FORM_COMPLETION_STATUS,
          diyData().measurements,
          measurementStatus()
        );
        if (!(currentFormCompletionStatus == FORM_COMPLETION_STATUS.EMPTY)) {
          const cursor = getFirstInvalidMeasurementCursor(
            diyData().measurements,
            measurementStatus()
          );
          setFormCursor(cursor);
        }
      }
    });
  };

  const getSelectedStandardSize = () => {
    const sizeData = standardSizeData().measurements;
    const anchorGraded = Object.values(sizeData.anchorGraded)[0];
    const nonAnchorUngraded = sizeData.nonAnchorUngraded;
    const selected = anchorGraded.selected;
    const selectedFit = fit().selected;
    const anchorKey = anchorGraded.label ? anchorGraded.label + ": " : "";
    return (
      <div class="flex flex-col  border border-frame bg-canvas-lightboxy-shadow">
        <span class="text-ink-link p-1 border-b border-frame">
          Standard Size Selected
        </span>
        <span class="p-1 flex flex-col">
          <span>
            {`${anchorKey} ${anchorGraded.values[selected]?.displayName[selectedFit]}`}
          </span>
          {Object.values(nonAnchorUngraded).map((obj) => {
            const objkey = obj.label ? obj.label + ": " : "";
            const objvalue = obj.selected ? (
              obj.displayNameTemplate.replace(
                "{{1}}",
                obj.selected?.replace(/-/g, ".")
              )
            ) : (
              <span class="text-red-500">Unselected</span>
            );
            return (
              <span>
                {objkey}
                {objvalue}
              </span>
            );
          })}
        </span>
      </div>
    );
  };

  const measurements = () =>
    Object.values(diyData().measurements).filter(
      (measurement) => measurement.isEnabled
    );

  const validationData = () =>
    measurements().map((measurement) => measurementStatus()[measurement.id]);

  const isDIYSelected = () =>
    measurementOptions().selected === "measure-it-yourself";
  const isStandardSizeSelected = () =>
    measurementOptions().selected == "standard-size";
  const isSendReferenceSelected = () =>
    measurementOptions().selected == "send-fitting-garment";
  const isSizeEmpty = () => measurementOptions().selected == "";

  const getReferenceLabels = () => {
    return sendRefData().radio.options[sendRefData().radio.selected];
  };

  return (
    <div class="flex flex-col items-center">
      <div
        class="w-full flex items-start flex-col sm:p-0 cursor-pointer hover:bg-canvas-light"
        onClick={handleClick}
      >
        <div class="flex-auto pr-2 px-1 text-sm sm:text-base font-bold uppercase">
          Size
        </div>
        <div class="flex-auto flex w-full justify-between items-center gap-2  py-2 px-3">
          <Show when={isSizeEmpty()}>
            <div class="text-ink w-full justify-start text-lg flex items-center">{`Select a size`}</div>
          </Show>

          <Show when={isDIYSelected()}>
            <div class="text-ink-link flex flex-col border border-frame bg-canvas-lightboxy-shadow">
              <span class="border-b border-frame p-1">Custom size selected</span>
              <div class="p-1">
                <ValidationBar
                  validationData={validationData()}
                  inline={true}
                />
              </div>
            </div>
          </Show>

          <Show when={isSendReferenceSelected()}>
            <div class="flex flex-col border border-frame bg-canvas-lightboxy-shadow">
              <div class="text-ink-link border-b border-frame p-1">
                Reference garment selected
              </div>
              <div class="p-1">
                {
                  sendRefData().radio.options[sendRefData().radio.selected]
                    ?.message
                }
              </div>
            </div>
          </Show>

          <Show when={isStandardSizeSelected()}>
            <div>{getSelectedStandardSize()}</div>
          </Show>
          <div class="flex justify-center items-center">
            <Switch>
              <Match when={isSizeEmpty()}>
                <RightArrow color="fill-secondary-accent" />
              </Match>
              <Match when={!isSizeEmpty()}>
                <RightArrow color="fill-secondary-900" />
              </Match>
            </Switch>
          </div>
        </div>
      </div>

      <Show
        when={errorMessage() != undefined && errorMessage()?.trim().length > 0}
      >
        <div class="text-error-100 bg-error-600 w-full text-left py-1 px-2 flex justify-between items-center">
          <div innerHTML={errorMessage()} />
          <div
            onClick={handleClick}
            class="bg-canvas text-error-600 hover:bg-canvas-lightpx-1 rounded-full cursor-pointer"
          >
            Fix it
          </div>
        </div>
      </Show>
    </div>
  );
};

export const DesktopCustomizationFields = (props) => {
  const options = () => props.customization.options;
  const selectedOption = () => options()[props.customization.selected];

  const {
    navBarData: {
      handlers: { handleNavBarChange, handleNavBarVisiblity },
    },
  } = useHeroForm();

  const handleSelection = (selectedOption) => {
    if (props.type === 'FIT') {
      props.handleSelection(selectedOption)
    } else {
      const id = props.customization.id || handleize(props.customization.label);
      props.handleSelection(id, selectedOption)
    }
  };

  return (
    <div class="w-full flex-col border-b-inner border-solid border-frame cursor-pointer hidden sm:flex gap-1 px-2 sm:px-6 pt-2 pb-4">
      <div class="flex-auto font-medium ">{props.customization.label}</div>
      <div class="flex-auto flex flex-wrap gap-2">
        <For each={Object.values(options())}>
          {(option, index) => (
            <Show when={option.isEnabled == undefined || option.isEnabled}>
              <OptionCard
                selectedValue={props.customization.selected}
                option={option}
                handleSelection={handleSelection}
                className="text-xs"
              />
            </Show>
          )}
        </For>
      </div>
    </div>
  );
};

const HeroFormFields = () => {
  const {
    color: {
      color
    },
    sizeSettings: {
      sizeData,
      errorMessage,
      diyData,
      measurementsInfo,
      measurementOptions,
      handlers: { handleMinMaxChangeOnFit },
    },
    styleCustomizations: {
      styleCustomizations,
      handlers: { handleStyleSelection },
    },
    fit: {
      fit,
      handlers: { handleFitSelection },
    },
    handlers: { handleCustomizationChange },
    sizeOption: { sizeOption },
    getters: { getExperimentFlags, isExperimentEnabled },
    accentCustomizations: {
      accentSettings,
      accentFormData,
      handlers
    },
    heroSectionSettings
  } = useHeroForm();

  let formFieldRef;
  const handleFitSelectionProxy = (selectedOption) => {
    batch(() => {
      handleFitSelection(selectedOption);
      handleCustomizationChange(diyData(), measurementsInfo, {
        sizeCustomization: sizeData()["selected"],
        fitCustomization: fit()["selected"],
        styleCustomizations: styleCustomizations(),
        measurementOption: measurementOptions()["selected"],
      });
      handleMinMaxChangeOnFit(selectedOption);
    });
  };

  const handleStyleSelectionProxy = (id, selectedOption) => {
    handleStyleSelection(id, selectedOption);
    handleCustomizationChange(diyData(), measurementsInfo, {
      sizeCustomization: sizeData()["selected"],
      fitCustomization: fit()["selected"],
      styleCustomizations: styleCustomizations(),
      measurementOption: measurementOptions()["selected"],
    });
  };

  const customizationsArr = () => {
    let stylesArr = Object.values(styleCustomizations())
      .filter(
        (customization) =>
          Object.values(customization.options).some((option) => option.isEnabled)
      )
      .map((customization) => {
        return {
          customization,
          selectionHandler: handleStyleSelectionProxy,
          type: 'STYLE'
        };
      });
    // Append fit customization
    if (Object.keys(fit().options).length > 1) {
      stylesArr.push({
        customization: fit(),
        selectionHandler: handleFitSelectionProxy,
        type: 'FIT'
      });
    }
    return stylesArr;
  };

  createEffect(() => {
    if (errorMessage() != '') {
      document.querySelector('#error-message')?.scrollIntoView({
        behavior: 'smooth'
      })
    }
  })

  onMount(() => {
    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.2 });
    observer.observe(formFieldRef)
    function handleIntersection(entries, observer) {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          window.posthog?.capture('product_form_seen', {
            experiment_flags: window.experimentFlags
          })
          window.dataLayer?.push({
            event: "product_form_seen"
          })
          observer.unobserve(entry.target);
        }
      });
    }
  })
  return (

    <div ref={formFieldRef} class="">
      <SizeButtonWithTabs
        context={useHeroForm}
      />
      <CustomizationMainForm />
      <CustomizationStatusV2 />
      <AccentContainerV2
        accentSettings={accentSettings}
        accentFormData={accentFormData}
        handlers={handlers}
      />
      {/* <ColorStatus
        class="max-w-lg mx-2 sm:mx-6 p-2 border-frame border boxy-shadow-sm bg-canvas-light"
        color={color().options[color().selected]?.label}
        swatch={color().options[color().selected]?.swatchImages?.src}
      /> */}
      <Show
        when={errorMessage() != undefined && errorMessage()?.trim().length > 0}
      >
        <div id="error-message" class="text-error-100 bg-error-600 scroll-mt-6 text-left flex justify-between items-center border-dashed max-w-lg mx-2 sm:mx-6 p-2 border-frame border boxy-shadow-sm">
          <div innerHTML={errorMessage()} />
          {/* <div onClick={handleClick} class="bg-canvas text-error-600 hover:bg-secondary-50 px-1 rounded-full cursor-pointer">Fix it</div> */}
        </div>
      </Show>
    </div>
  );
};

export const SelectedColor = () => {
  const {
    color: { color },

  } = useHeroForm();
  const selectedColor = () => color().options[color().selected];
  return (
    <a
      href="#page-top"
      class="sm:landscape:hidden w-full border-y-inner border-frame  px-2 bg-canvas-depressed flex gap-2 justify-center py-[4px] items-center text-secondary-700"
    >
      <span class="font-mono text-xs">Color:</span>{" "}
      <img
        alt={selectedColor().label}
        class="rounded-full h-3 w-3 inline-flex border-white border-4 lazyload"
        loading="lazy"
        src={`${selectedColor().swatchImages.src}&width=16`}
        data-srcset={[34, 49, 58, 68].map(
          (width) =>
            `${selectedColor().swatchImages.src}&width=${width} ${width}w`
        )}
      />{" "}
      <span class="text-xs font-mono">{selectedColor().label}</span>{" "}
    </a>
  );
};

export default function ProductHeroFormFields() {
  const {
    productInfo,
    color: {
      color
    },
    reviewProfilePictures,
    navBarData: {
      navBarData
    },
    getters: {
      getExperimentFlags,
      isExperimentEnabled,
      getFeatureFlagPayloadProp
    }
  } = useHeroForm();

  let featuredReviewParentRef, rtbRef, rtbMountRef;

  // mount the RTB block when its slot comes near the viewport (falls back to immediate mount)
  const [rtbNear, setRtbNear] = createSignal(typeof IntersectionObserver === 'undefined');
  onMount(() => {
    if (!rtbMountRef || rtbNear()) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      setRtbNear(true);
    }, { rootMargin: RTB_MOUNT_ROOT_MARGIN });
    observer.observe(rtbMountRef);
    onCleanup(() => observer.disconnect());
  });

  const [modalRequested, setModalRequested] = createSignal(false);
  createEffect(() => {
    if (navBarData().isVisible) setModalRequested(true);
  });

  onMount(() => {
    const prefetchModal = () => { loadCustomizationModal().catch(() => {}); };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(prefetchModal, { timeout: MODAL_PREFETCH_IDLE_TIMEOUT_MS });
    } else {
      setTimeout(prefetchModal, MODAL_PREFETCH_IDLE_TIMEOUT_MS);
    }
  });

  onMount(() => {
    if (productInfo.featuredReviews) {
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !window.isFeaturedReviewVisited) {
            window.isFeaturedReviewVisited = true;
            window.posthog?.capture('viewed_featured_review_section', {
              experiment_flags: window.experimentFlags
            })
          }
        });
      }, { threshold: 0.5 });

      observer.observe(featuredReviewParentRef);
      onCleanup(() => {
        observer.disconnect();
      })
    }

    if (productInfo.rtb) {
      const rtbObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !window.isRTBAboveCTAVisited) {
            window.isRTBAboveCTAVisited = true;
            window.posthog?.capture('viewed_rtb_above_cta', {
              experiment_flags: window.experimentFlags
            })
          }
        });
      }, { threshold: 0.5 });

      rtbObserver.observe(rtbRef);
      onCleanup(() => {
        rtbObserver.disconnect();
      })
    }

  })

  const handleReviewsClick = (event) => {
    event.preventDefault();
    const reviews = document.querySelector('#reviews');
    if (reviews) {
      reviews.scrollIntoView();
    }
  }

  const currentAverageRating = () => {
    const colorSelected = color()['selected'];
    const variantAverageRating = productInfo.variants.standard[colorSelected].averageRating;
    const variantTotalReviewCount = productInfo.variants.standard[colorSelected].ratingStats?.total_count ?? null;

    if (variantAverageRating != null && variantTotalReviewCount != null && variantTotalReviewCount >= 5) {
      return parseFloat(variantAverageRating).toFixed(1);
    }

    const productAverageRating = productInfo.averageRating;
    const productTotalReviewCount = productInfo.ratingStats?.total_count ?? null;
    if (productAverageRating != null && productTotalReviewCount != null && productTotalReviewCount >= 5) {
      return parseFloat(productAverageRating).toFixed(1)
    }

    return typeof (averageRating) != 'undefined' ? parseFloat(averageRating).toFixed(1) : null;
  }

  function getRandomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function getRandomTwoNumbersInRange(min, max) {
    if (max - min < 1) {
      return [];
      throw new Error("Range is too small to get two unique numbers.");
    }

    let num1 = getRandomNumberInRange(min, max);
    let num2;

    do {
      num2 = getRandomNumberInRange(min, max);
    } while (num1 === num2);

    return [num1, num2];
  }

  return (
    <div id="product-form" class="">
      <div class="sm:landscape:border-t-0 border-frame">
        <HeroFormFields />
        <HeroFormSubmitGroup
          context={useHeroForm}
          class={`max-w-lg mx-2 sm:mx-6 pt-2 !border-frame gap-0`}
          classList={{
            "px-2 !bg-canvas-light border boxy-shadow-sm": !heroSectionSettings.isNewVersion
          }}
        />
        <TrustPolicy
          class="max-w-lg sm:mx-6 -mt-2"
          isNewVersion={heroSectionSettings.isNewVersion}
          {...heroSectionSettings.trustPolicy}
        />
      </div>
      <div class="px-2 sm:px-6 pb-4 border-frame">
        <div class="" ref={featuredReviewParentRef}>
          <a
            classList={{
              "hidden": heroSectionSettings.isNewVersion
            }}
            href="#reviews" onclick={handleReviewsClick}>
            <div class="max-w-lg w-auto justify-center text-center mt-2 flex items-center text-sm gap-1">
              <span class="text-sm flex items-center gap-0.5 fill-primary-accent">
                <Stars rating={currentAverageRating()} class="flex" starClass="w-[20px] h-[20px] sm:w-3 sm:h-3 fill-[#CDB476]" />
                <span class="font-medium text-2xs sm:text-sm">Rated {currentAverageRating()?.replace('.0', '')}/5 by 100+ customers</span>
                <div class="flex"><For each={getRandomTwoNumbersInRange(0, reviewProfilePictures.length - 1)}>{(ind, index) => (
                  <img
                    class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-solid border-canvas rounded-full object-cover"
                    style={`margin-left: -${8 * index()}px;`}
                    src={reviewProfilePictures[ind]}
                  />
                )}</For></div>
              </span>
            </div>
          </a>
          <div ref={rtbRef}>
            {/* <Show when={productInfo.rtb}>
              <RTBVideoFloater
                rtb={productInfo.rtb ?? {}}
              />
            </Show> */}
          </div>
          {/* rtb_v2 is serialized as an array; an empty one used to mount the block (and now would fetch its chunk) for nothing */}
          <Show when={Array.isArray(productInfo?.rtb_v2) ? productInfo.rtb_v2.length > 0 : !!productInfo?.rtb_v2}>
            <div ref={rtbMountRef}>
              <Show when={rtbNear()}>
                <Suspense fallback={null}>
                  <RTB sectionId={productInfo?.sectionId} rtb_v2={productInfo?.rtb_v2 ?? {}} />
                </Suspense>
              </Show>
            </div>
          </Show>
          {/* <div class="mt-2">
            <HeroFormSubmitGroup
              context={useHeroForm}
            />
          </div> */}
          <Show
            when={heroSectionSettings.isNewVersion}
            fallback={(
              <div>
                <div class="max-w-lg flex flex-col gap-2">
                  <BrandValues values={productInfo.brandValues} />
                </div>
              </div>
            )}
          >
            <div class="max-w-xl flex flex-col gap-2">
              <BrandValuesV2 values={productInfo.brandValues} />
            </div>
          </Show>
        </div>
      </div>
      <Show when={modalRequested()}>
        <Suspense fallback={null}>
          <MobileCustomizationsModal />
        </Suspense>
      </Show>
    </div>
  );
};




